import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  DUPLICATE_EVENT_INFO_ROUTE,
  EVENT_CREATE_INFO_ROUTE,
  EVENT_EDIT_DETAILS_ROUTE,
  EVENT_LIST,
  EVENT_REPORT_ROUTE,
  OFFERS_ROUTE,
} from '@constants/routes';
import { FeatureFlagContext } from '@contexts/feature-flag/feature-flag-context';
import { Event, EventListItem } from '@domain/event';
import { GET_EVENT_QUERY_KEY } from '@hooks/events/use-get-event';
import { useToast } from '@hooks/use-toast/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { createPreviewUrl } from '@utils/create-paths/create-preview-url';
import { isEventScheduled } from '@utils/is-event-scheduled/is-event-scheduled';

import type { UseEventNavigationParams } from './use-event-navigation.type';

export const VIEW_UPCOMING = 'upcoming';
export const VIEW_LIVE = 'live';
export const VIEW_PAST = 'past';
export const VIEW_DRAFT = 'draft';

export const useEventNavigation = ({
  companyId,
  clubState,
  view,
  defaultChannelSlug,
  language,
}: UseEventNavigationParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { isWebView } = useContext(FeatureFlagContext);
  const isDraftView = view === VIEW_DRAFT;
  const isUpcomingView = view === VIEW_UPCOMING;

  const createPathToNewReport = (event: EventListItem) => {
    const route = EVENT_REPORT_ROUTE;
    const navigationNewReportRoute = route
      .replace(':companyId', companyId)
      .replace(':clubSlug', event.club.slug)
      .replace(':eventId', event.id);

    return navigationNewReportRoute;
  };

  const createPathToReport = (event: EventListItem) => {
    return createPathToNewReport(event);
  };

  const createPathToEdit = (event: EventListItem) => {
    const route = isUpcomingView ? OFFERS_ROUTE : EVENT_EDIT_DETAILS_ROUTE;
    const navigationEditRoute = route
      .replace(':companyId', companyId)
      .replace(':clubSlug', event.club.slug)
      .replace(':eventId', event.id);

    return navigationEditRoute;
  };

  const createPathToDuplicate = (event: EventListItem) => {
    return DUPLICATE_EVENT_INFO_ROUTE.replace(':companyId', companyId)
      .replace(':clubSlug', event.club.slug)
      .replace(':eventId', event.id);
  };
  const duplicateEvent = (event: EventListItem) => {
    navigate(createPathToDuplicate(event));
  };

  const navigateToCreate = () => {
    navigate(EVENT_CREATE_INFO_ROUTE.replace(':companyId', companyId as string).replace(':clubSlug', clubState.slug));
  };

  const navigateToEdit = (event: EventListItem) => {
    navigate(createPathToEdit(event));
  };

  const preSeedEventCache = (event: EventListItem) => {
    const queryKey = [GET_EVENT_QUERY_KEY, event.id];
    const partialEvent = {
      id: event.id,
      legacyId: event.legacyId,
      name: event.name,
      slug: event.slug,
      startingTime: event.startingTime,
      endingTime: event.endingTime,
      doorsOpening: event.doorsOpening,
      coverUrl: event.coverUrl,
      coverAspectRatio: event.coverAspectRatio,
      isFestival: event.isFestival,
      club: event.club,
      companyClub: event.companyClub,
      settings: event.settings,
      ticketMaxCapacity: event.ticketMaxCapacity,
      soldItems: event.soldItems,
      scheduledAt: event.scheduledAt,
      offerSalesStartAt: event.offerSalesStartAt,
      currency: event.currency,
    } as unknown as Event;

    queryClient.setQueryData<Event>(queryKey, (old) => old ?? partialEvent);
    queryClient.invalidateQueries({ queryKey });
  };

  const navigateToReport = (event: EventListItem) => {
    if (isDraftView) {
      return navigateToEdit(event);
    }
    preSeedEventCache(event);
    navigate(createPathToReport(event));
  };

  const navigateToTab = ({ slug, id }: { slug: string; id: string }) => {
    navigate(
      EVENT_LIST.replace(':companyId', companyId as string)
        .replace(':clubSlug', slug as string)
        .replace(':view', id)
    );
  };

  const navigateToB2CEvent = (event: EventListItem | Event, slug?: string) => {
    if (!defaultChannelSlug) {
      if (toast?.data[0]?.type === 'error') toast?.remove(toast?.data[0]?.id);
      toast.push({
        title: t('eventList:defaultChannelRequired'),
        text: t('eventList:defaultChannelRequiredDescription'),
        type: 'error',
        disableTimeOut: true,
      });
      return;
    }

    const isScheduledEvent = isEventScheduled(event.settings.isPublished, event.scheduledAt, event.club.city.timezone);

    const previewUrl = createPreviewUrl({
      event,
      locale: language,
      defaultChannel: slug ?? defaultChannelSlug,
      isScheduledEvent,
      isWebView,
    });

    globalThis.open(previewUrl, '_blank');
  };

  return {
    navigateToCreate,
    navigateToEdit,
    navigateToReport,
    navigateToB2CEvent,
    navigateToTab,
    duplicateEvent,
    createPathToReport,
    createPathToNewReport,
    createPathToEdit,
    createPathToDuplicate,
  };
};
